import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpBackend, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { CommonModule, registerLocaleData } from '@angular/common';
import { RouterModule, TitleStrategy } from '@angular/router';
import { TranslateModule, TranslateLoader, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

import { AppComponent } from './app.component';
import { NewsComponent } from './news/news.component';
import { AnnouncementComponent } from './announcement/announcement.component';
import { PortalComponent } from './portal/portal.component';
import { CompanyNewsListComponent } from './news/company-news-list/company-news-list.component';
import { NewsSlideoutComponent } from './news/news-slideout/news-slideout.component';
import { NewsItemComponent } from './news/news-item/news-item.component';

import { AihLaunchpadCoreModule, AuthConfigModule, Configuration, AuthSettings, LanguageService, LocalisedDatePipe, TranslatedTitleStrategy} from 'aih-launchpad-core';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { SlipcaseNewsListComponent } from './news/slipcase-news-list/slipcase-news-list.component';

import localeNl from '@angular/common/locales/nl';
import { AdminModule } from './admin/admin.module';
import { AppsCardComponent } from "./apps-card/apps-card.component";

import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from "ng-recaptcha-2";
import { NewsCategoryPipe } from "./news/news-category.pipe";
import { NewsStateService } from './news/news-state.service';
import { NEWS_STATE_CONFIG } from './news/news-state.config';
import { QuillModule } from "ngx-quill";
import { QuillLinkFormat } from './utils/quill-link.format';

export function HttpLoaderFactory(http:HttpBackend){
  return new MultiTranslateHttpLoader(http, [
    { prefix: './assets/i18n/', suffix: '.json' },
    { prefix: './assets/i18n/datepicker/', suffix: '.json' },
    { prefix: './assets/locale/i18n/', suffix: '.json' },
    { prefix: './assets/locale/i18n/disclaimer/', suffix: '.json' },
    { prefix: './assets/locale/i18n/cms/', suffix: '.json' },
  ]);
}

registerLocaleData(localeNl, 'nl');

@NgModule({
  declarations: [
    AppComponent,
    PortalComponent,
    SlipcaseNewsListComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    RouterModule,
    CommonModule,
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpBackend]
      }
    }),
    AihLaunchpadCoreModule.forRoot({
      appName: environment.appName,
      homeUrl: environment.homeUrl,
      loginUrl: environment.loginUrl,
      cookieDomain: environment.cookieDomain,
      launchpadApi: environment.launchpadGraphEndpoint,
      launchpadBaseApi: environment.launchpadApi,
      authSettings: environment.authSettings,
      timeoutInMins: environment.timeoutInMins,
      impersonationEnabled: environment.impersonationEnabled,
      languageSelectorEnabled: environment.languageSelectorEnabled
    } as Configuration),
    AuthConfigModule.forRoot(environment.authSettings as AuthSettings),
    AdminModule,
    LocalisedDatePipe,
    TranslatePipe,
    AppsCardComponent,
    RecaptchaV3Module,
    NewsCategoryPipe,
    CompanyNewsListComponent,
    NewsItemComponent,
    NewsComponent,
    QuillModule.forRoot({
      customModules: [{ path: 'formats/link', implementation: QuillLinkFormat }]
    }),
    NewsSlideoutComponent,
    AnnouncementComponent
],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptcha.siteKey
    },
    NewsStateService,
    { provide: NEWS_STATE_CONFIG, useValue: { pageSize: 10 } },
    { provide: TitleStrategy, useClass: TranslatedTitleStrategy },
    // Preload the active language before the app renders, so the first paint (card titles, nav,
    // page/tab title) shows translated text instead of raw keys like "appName".
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      const language = inject(LanguageService);
      const lang = language.getLastLanguage() || 'en';
      return firstValueFrom(translate.use(lang)).catch(() => undefined);
    }),
  ]
})

export class AppModule {
  constructor(){
    console.log('environment settings ',environment);
  }
 }
